import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Button, Card } from "@project-mgmt/ui";
import { useInviteUser } from "../api/workspaces";
const ROLE_OPTIONS = [
    {
        value: "project_manager",
        label: "Project Manager",
        description: "Can manage projects and tasks",
    },
    {
        value: "client",
        label: "Client",
        description: "Can view assigned projects and provide feedback",
    },
    {
        value: "contractor",
        label: "Contractor",
        description: "Can work on assigned tasks and update status",
    },
];
export function InviteForm({ workspaceId, onSuccess, onCancel, className = "" }) {
    const [formData, setFormData] = useState({
        phoneNumber: "",
        role: "contractor",
    });
    const inviteUser = useInviteUser(workspaceId);
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.phoneNumber.trim()) {
            return;
        }
        try {
            await inviteUser.trigger(formData);
            onSuccess?.();
            // Reset form
            setFormData({
                phoneNumber: "",
                role: "contractor",
            });
        }
        catch (err) {
            // Error is handled by the hook
            console.error("Failed to invite user:", err);
        }
    };
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };
    const formatPhoneNumber = (value) => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, "");
        // Format as +1 (XXX) XXX-XXXX for US numbers
        if (digits.length <= 10) {
            if (digits.length >= 6) {
                return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
            }
            else if (digits.length >= 3) {
                return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
            }
            else if (digits.length > 0) {
                return `+1 (${digits}`;
            }
        }
        // For international numbers, just add + prefix
        return digits.length > 0 ? `+${digits}` : "";
    };
    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        handleInputChange("phoneNumber", formatted);
    };
    return (_jsx(Card, { className: `p-6 ${className}`, children: _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx("h2", { className: "mb-4 text-lg font-semibold", children: "Invite User to Workspace" }), _jsx("p", { className: "mb-4 text-sm text-gray-600", children: "Send an invitation to join this workspace. They'll receive access based on the role you assign." })] }), inviteUser.error && (_jsx("div", { className: "rounded-md border border-red-200 bg-red-50 p-3", children: _jsx("div", { className: "text-sm text-red-800", children: inviteUser.error instanceof Error ? inviteUser.error.message : "Failed to send invitation" }) })), inviteUser.data && (_jsx("div", { className: "rounded-md border border-green-200 bg-green-50 p-3", children: _jsx("div", { className: "text-sm text-green-800", children: "Invitation sent successfully!" }) })), _jsxs("div", { children: [_jsx("label", { htmlFor: "phoneNumber", className: "mb-1 block text-sm font-medium text-gray-700", children: "Phone Number *" }), _jsx("input", { type: "tel", id: "phoneNumber", value: formData.phoneNumber, onChange: handlePhoneChange, placeholder: "+1 (555) 123-4567", className: "w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500", required: true, disabled: inviteUser.isLoading }), _jsx("div", { className: "mt-1 text-xs text-gray-500", children: "Enter the phone number they use to sign in" })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "role", className: "mb-1 block text-sm font-medium text-gray-700", children: "Role *" }), _jsx("select", { id: "role", value: formData.role, onChange: (e) => handleInputChange("role", e.target.value), className: "w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500", required: true, disabled: inviteUser.isLoading, children: ROLE_OPTIONS.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value))) }), _jsx("div", { className: "mt-2 space-y-1", children: ROLE_OPTIONS.map((option) => (_jsxs("div", { className: `rounded p-2 text-xs ${formData.role === option.value ? "border border-blue-200 bg-blue-50 text-blue-700" : "text-gray-500"}`, children: [_jsxs("span", { className: "font-medium", children: [option.label, ":"] }), " ", option.description] }, option.value))) })] }), _jsxs("div", { className: "flex gap-3 pt-4", children: [_jsx(Button, { type: "submit", disabled: inviteUser.isLoading || !formData.phoneNumber.trim(), className: "flex-1", children: inviteUser.isLoading ? (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: "h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" }), "Sending Invitation..."] })) : ("Send Invitation") }), onCancel && (_jsx(Button, { type: "button", variant: "outline", onClick: onCancel, disabled: inviteUser.isLoading, children: "Cancel" }))] })] }) }));
}
